import type { ReactNode } from 'react';

/** The docs content, one entry per page.
 *
 *  Facts are checked against the code, not written from memory -- a docs page
 *  that lies costs more than no docs page:
 *    POST /v1/emails, 202 Accepted            app/api/v1/emails.py
 *    `to` is an array, 1..50                  SendEmailRequest, api/v1/schemas.py
 *    one of html or text required             send_email(), emails.py
 *    mg_live_ / mg_test_ prefixes             app/core/security.py
 *    SMTP smtp.mailgiri.com:587, key as the
 *      password, username ignored             app/smtp/server.py
 *    the nine event types                     dashboard/src/pages/Webhooks.tsx
 *    plan limits (fetched, not copied)        GET /v1/pricing
 */

export type CodeLine = { t?: 'mist' | 'amber' | 'wire'; s: string };
export type Sample = { label: string; lines: CodeLine[] };

export type Block =
  | { kind: 'p'; body: ReactNode }
  | { kind: 'note'; body: ReactNode }
  | { kind: 'h2'; body: string }
  // Filled at render time from GET /v1/pricing. A block rather than a table of
  // literals because this page was the second place the plan limits were
  // written down, and two copies of a number are two numbers.
  | { kind: 'plan-limits' }
  | { kind: 'list'; items: ReactNode[] }
  | { kind: 'chips'; items: string[] }
  | { kind: 'code'; title: string; lines: CodeLine[] }
  | { kind: 'tabs'; samples: Sample[] }
  | { kind: 'table'; head: string[]; rows: string[][] };

export type DocPage = {
  slug: string;
  title: string;
  summary: string;
  blocks: Block[];
};

const m = (s: string): CodeLine => ({ t: 'mist', s });
const plain = (s: string): CodeLine => ({ s });

// ---------------------------------------------------------------- send samples

const CURL: Sample = {
  label: 'curl',
  lines: [
    m('# the id comes back immediately; delivery is reported as events'),
    plain('curl -X POST https://api.mailgiri.com/v1/emails \\'),
    plain('  -H "Authorization: Bearer mg_live_your_key" \\'),
    plain('  -H "Content-Type: application/json" \\'),
    plain("  -d '{"),
    plain('    "from": "hello@yourapp.com",'),
    plain('    "to": ["user@example.com"],'),
    plain('    "subject": "Welcome",'),
    plain('    "html": "<p>Glad you are here.</p>"'),
    plain("  }'"),
  ],
};

const NODE: Sample = {
  label: 'Node',
  lines: [
    plain("const res = await fetch('https://api.mailgiri.com/v1/emails', {"),
    plain("  method: 'POST',"),
    plain('  headers: {'),
    plain("    Authorization: 'Bearer mg_live_your_key',"),
    plain("    'Content-Type': 'application/json',"),
    plain('  },'),
    plain('  body: JSON.stringify({'),
    plain("    from: 'hello@yourapp.com',"),
    plain("    to: ['user@example.com'],"),
    plain("    subject: 'Welcome',"),
    plain("    html: '<p>Glad you are here.</p>',"),
    plain('  }),'),
    plain('});'),
    plain(''),
    m('// { id: "msg_9f2a1c", status: "queued" }'),
    plain('const { id } = await res.json();'),
  ],
};

const PYTHON: Sample = {
  label: 'Python',
  lines: [
    plain('import httpx'),
    plain(''),
    plain('res = httpx.post('),
    plain('    "https://api.mailgiri.com/v1/emails",'),
    plain('    headers={"Authorization": "Bearer mg_live_your_key"},'),
    plain('    json={'),
    plain('        "from": "hello@yourapp.com",'),
    plain('        "to": ["user@example.com"],'),
    plain('        "subject": "Welcome",'),
    plain('        "html": "<p>Glad you are here.</p>",'),
    plain('    },'),
    plain(')'),
    plain('print(res.json())'),
  ],
};

// ---------------------------------------------------------------- SMTP samples

const SMTP_NODE: Sample = {
  label: 'Nodemailer',
  lines: [
    plain("import nodemailer from 'nodemailer';"),
    plain(''),
    plain('const transport = nodemailer.createTransport({'),
    plain("  host: 'smtp.mailgiri.com',"),
    plain('  port: 587,'),
    m('  // STARTTLS on 587: begin in the clear, upgrade before AUTH.'),
    plain('  secure: false,'),
    plain('  requireTLS: true,'),
    plain('  auth: {'),
    m('    // The username is ignored. The API key is the password.'),
    plain("    user: 'apikey',"),
    plain("    pass: 'mg_live_your_key',"),
    plain('  },'),
    plain('});'),
    plain(''),
    plain('await transport.sendMail({'),
    plain("  from: 'hello@yourapp.com',"),
    plain("  to: 'user@example.com',"),
    plain("  subject: 'Welcome',"),
    plain("  html: '<p>Glad you are here.</p>',"),
    plain('});'),
  ],
};

const SMTP_PYTHON: Sample = {
  label: 'Python',
  lines: [
    plain('import smtplib'),
    plain('from email.message import EmailMessage'),
    plain(''),
    plain('message = EmailMessage()'),
    plain('message["From"] = "hello@yourapp.com"'),
    plain('message["To"] = "user@example.com"'),
    plain('message["Subject"] = "Welcome"'),
    plain('message.set_content("Glad you are here.")'),
    plain('message.add_alternative("<p>Glad you are here.</p>", subtype="html")'),
    plain(''),
    plain('with smtplib.SMTP("smtp.mailgiri.com", 587) as smtp:'),
    m('    # Upgrade before AUTH, so the key never crosses in the clear.'),
    plain('    smtp.starttls()'),
    m('    # Username ignored; the API key is the password.'),
    plain('    smtp.login("apikey", "mg_live_your_key")'),
    plain('    smtp.send_message(message)'),
  ],
};

const SMTP_PHP: Sample = {
  label: 'PHP',
  lines: [
    plain('use Symfony\\Component\\Mailer\\Mailer;'),
    plain('use Symfony\\Component\\Mailer\\Transport;'),
    plain(''),
    m('// Username ignored, API key as the password.'),
    plain("$dsn = 'smtp://apikey:mg_live_your_key@smtp.mailgiri.com:587';"),
    plain('$mailer = new Mailer(Transport::fromDsn($dsn));'),
  ],
};

const SMTP_ENV: Sample = {
  label: 'Config',
  lines: [
    m('# Django, Rails, Laravel, or anything that reads env vars'),
    plain('SMTP_HOST=smtp.mailgiri.com'),
    plain('SMTP_PORT=587'),
    plain('SMTP_USER=apikey'),
    plain('SMTP_PASSWORD=mg_live_your_key'),
    plain('SMTP_TLS=starttls'),
  ],
};

const WEBHOOK_VERIFY: Sample = {
  label: 'Python',
  lines: [
    plain('import hashlib'),
    plain('import hmac'),
    plain(''),
    plain('def verify(raw_body: bytes, signature: str, secret: str) -> bool:'),
    plain('    expected = hmac.new('),
    plain('        secret.encode(), raw_body, hashlib.sha256'),
    plain('    ).hexdigest()'),
    m('    # compare_digest, not ==: a plain comparison returns early on the'),
    m('    # first wrong byte, which leaks the signature a byte at a time.'),
    plain('    return hmac.compare_digest(expected, signature)'),
  ],
};

// ------------------------------------------------------------------- the pages

export const DOC_PAGES: DocPage[] = [
  {
    slug: 'introduction',
    title: 'Introduction',
    summary:
      'Email infrastructure for applications: a REST API and an SMTP relay that share one set of rules.',
    blocks: [
      {
        kind: 'p',
        body: 'mailgiri sends transactional and marketing email. Everything the API does, the SMTP relay does too — the same suppression list, the same event stream, the same delivery tracking — so switching between them is a configuration change rather than a rewrite.',
      },
      { kind: 'h2', body: 'Two ways in' },
      {
        kind: 'list',
        items: [
          <>
            <strong>REST API</strong> — one POST per send, with batching, scheduling,
            attachments and tags. Start here for anything new.
          </>,
          <>
            <strong>SMTP relay</strong> — point an existing mail configuration at us. No
            code change, and the same rules apply.
          </>,
        ],
      },
      { kind: 'h2', body: 'What you get on signup' },
      {
        kind: 'p',
        body: 'A live key, a test key, and a sending domain that is already verified — so the first send works before any DNS is configured. A custom domain can be added later without interrupting it.',
      },
      {
        kind: 'note',
        body: 'The live key is shown once, at signup, and only its hash is stored. If it is lost, issue a new one; nobody can recover the original.',
      },
    ],
  },

  {
    slug: 'send-an-email',
    title: 'Send an email',
    summary: 'One POST, and the message id comes back immediately.',
    blocks: [
      { kind: 'tabs', samples: [CURL, NODE, PYTHON] },
      {
        kind: 'note',
        body: (
          <>
            The response is <code>202 Accepted</code>, not <code>200</code>. The message is
            queued and the id is how you follow it — the receiving server has not answered
            yet, so delivery is reported through events rather than this response.
          </>
        ),
      },
      { kind: 'h2', body: 'Fields' },
      {
        kind: 'table',
        head: ['Field', 'Required', 'Notes'],
        rows: [
          ['from', 'yes', 'An address on a domain you have added.'],
          ['to', 'yes', 'An array, even for one recipient. 1 to 50 addresses.'],
          ['subject', 'no', 'Omitted sends with an empty subject.'],
          ['html / text', 'one of', 'Send both when you can. See below.'],
          ['cc, bcc', 'no', 'Arrays, up to 50 each.'],
          ['reply_to', 'no', 'A single address.'],
          ['attachments', 'no', 'Up to 20, 25 MB total across the message.'],
          ['tags', 'no', 'Up to 20. Returned on every event for this message.'],
          ['scheduled_at', 'no', 'Must include a UTC offset.'],
          ['idempotency_key', 'no', 'Makes a retry safe.'],
        ],
      },
      { kind: 'h2', body: 'Always send a text part' },
      {
        kind: 'p',
        body: 'One of html or text is required, but sending only html costs you twice: spam filters weigh a missing text part against you, and readers on text-only clients get a blank message.',
      },
      { kind: 'h2', body: 'Retrying safely' },
      {
        kind: 'p',
        body: (
          <>
            A network timeout does not tell you whether the send happened. Pass an{' '}
            <code>idempotency_key</code> and a retry with the same key returns the original
            message instead of sending a second copy.
          </>
        ),
      },
      {
        kind: 'note',
        body: (
          <>
            <code>scheduled_at</code> is rejected without a UTC offset. A naive timestamp
            would be read in the server&apos;s timezone, which is a quiet way to send a
            campaign at the wrong hour.
          </>
        ),
      },
    ],
  },

  {
    slug: 'smtp',
    title: 'SMTP',
    summary: 'Point an existing mail configuration at the relay. No code change.',
    blocks: [
      {
        kind: 'p',
        body: 'If your application already speaks SMTP, this is the shortest path: change four settings and the same suppression rules, events and tracking apply as over the API.',
      },
      { kind: 'h2', body: 'Settings' },
      {
        kind: 'table',
        head: ['Setting', 'Value'],
        rows: [
          ['Host', 'smtp.mailgiri.com'],
          ['Port', '587 (STARTTLS)'],
          ['Username', 'Anything — it is ignored'],
          ['Password', 'Your API key (mg_live_… or mg_test_…)'],
          ['Encryption', 'STARTTLS, required'],
        ],
      },
      {
        kind: 'note',
        body: (
          <>
            The <strong>API key is the password</strong> and the username is ignored. That
            is deliberate: there is no second credential to rotate, and revoking a key
            closes this route at the same moment it closes the API.
          </>
        ),
      },
      { kind: 'h2', body: 'Examples' },
      { kind: 'tabs', samples: [SMTP_NODE, SMTP_PYTHON, SMTP_PHP, SMTP_ENV] },
      { kind: 'h2', body: 'Test mode works here too' },
      {
        kind: 'p',
        body: 'Authenticate with a test key and the relay runs the whole pipeline without sending real mail or spending quota — the same behaviour a test key gives over REST. It is the right credential for a staging environment.',
      },
      { kind: 'h2', body: 'If it will not connect' },
      {
        kind: 'list',
        items: [
          <>
            <strong>535 authentication failed</strong> — the password is the API key
            itself, not your dashboard password, and it must not be URL-encoded.
          </>,
          <>
            <strong>Connection hangs</strong> — many hosting providers block outbound 587.
            Check with your provider before assuming the relay is down.
          </>,
          <>
            <strong>Mail accepted but never arrives</strong> — check the suppression list
            first. A previously bounced address is dropped before it reaches the provider.
          </>,
          <>
            <strong>550 from address rejected</strong> — the <code>From</code> domain has
            to be one you have added and verified.
          </>,
        ],
      },
    ],
  },

  {
    slug: 'api-keys',
    title: 'API keys',
    summary: 'Live keys send mail. Test keys run everything except the sending.',
    blocks: [
      {
        kind: 'p',
        body: (
          <>
            Every account gets one of each, and the prefix says which is which:{' '}
            <code>mg_live_</code> or <code>mg_test_</code>.
          </>
        ),
      },
      { kind: 'h2', body: 'Test keys' },
      {
        kind: 'p',
        body: 'A test key runs the entire pipeline — suppression checks, event generation, webhook delivery, the message log — and sends no real mail, spends no quota. Use it in CI and in staging. It behaves the same over REST and over SMTP.',
      },
      { kind: 'h2', body: 'Scopes' },
      {
        kind: 'p',
        body: 'A key can be narrowed to only what it needs. A key used by a webhook receiver does not need permission to send, and narrowing it limits the damage if it leaks.',
      },
      {
        kind: 'note',
        body: 'Keys are shown once and stored only as hashes. Rotate by issuing a new key, moving traffic to it, then revoking the old one — revoking is immediate and closes SMTP access at the same time.',
      },
    ],
  },

  {
    slug: 'domains',
    title: 'Domains',
    summary: 'Send from your own domain, or from the platform subdomain with no setup.',
    blocks: [
      {
        kind: 'p',
        body: 'The subdomain provisioned at signup is already verified, so you can send immediately. Adding your own domain is what puts your brand in the From line and builds reputation you own.',
      },
      { kind: 'h2', body: 'Publish every record' },
      {
        kind: 'note',
        body: 'Adding only the DKIM records is the common mistake: verification passes and sending then fails, because the MAIL FROM MX record is missing. Publish all of them together.',
      },
      { kind: 'h2', body: 'Prefer a subdomain' },
      {
        kind: 'p',
        body: 'Send from mail.yourcompany.com rather than yourcompany.com. It keeps sending reputation separate from the domain your staff email from, so a bad campaign cannot damage your ability to do business.',
      },
    ],
  },

  {
    slug: 'webhooks',
    title: 'Webhooks',
    summary: 'Nine event types, pushed as they happen and signed so you can verify them.',
    blocks: [
      { kind: 'chips', items: [
        'send', 'delivery', 'bounce', 'complaint', 'open', 'click', 'reject',
        'delivery_delay', 'rendering_failure',
      ] },
      {
        kind: 'p',
        body: 'Subscribe an endpoint to the events you care about. Each delivery is signed with your endpoint secret, so you can confirm it came from us rather than from anyone who learned the URL.',
      },
      { kind: 'h2', body: 'Verifying a delivery' },
      { kind: 'tabs', samples: [WEBHOOK_VERIFY] },
      {
        kind: 'note',
        body: (
          <>
            Compute the HMAC over the <strong>raw request body</strong>, before any JSON
            parsing. Re-serialising changes the bytes — key order, whitespace — and the
            signature will not match.
          </>
        ),
      },
      { kind: 'h2', body: 'Endpoints must be https' },
      {
        kind: 'p',
        body: 'A plaintext endpoint is refused. Payloads carry recipient addresses and message metadata, and sending those over http hands them to anyone on the network path.',
      },
      { kind: 'h2', body: 'Respond fast, work later' },
      {
        kind: 'p',
        body: 'Return 2xx as soon as you have stored the event, and do the real work afterwards. A slow endpoint gets retried, which turns one event into several.',
      },
    ],
  },

  {
    slug: 'suppressions',
    title: 'Suppressions',
    summary: 'Why a send stops before it reaches the provider.',
    blocks: [
      {
        kind: 'p',
        body: 'A hard bounce or a spam complaint adds the address to your suppression list. Later sends to it are dropped before they reach the provider, and the message log records why.',
      },
      {
        kind: 'p',
        body: 'This is not a limitation to work around. Repeatedly mailing addresses that bounce is what moves a sending reputation from good to filtered, and the cost lands on every other message you send.',
      },
      { kind: 'h2', body: 'When an address should come back' },
      {
        kind: 'p',
        body: 'A mailbox that was full or a domain that was briefly misconfigured can be removed from the list. An address that produced a complaint should not be: someone told their provider they did not want your mail.',
      },
    ],
  },

  {
    slug: 'limits',
    title: 'Limits',
    summary: 'What each plan includes, and what happens at the edge.',
    blocks: [
      // Was a table of literals copied from app/core/plans.py, with a comment
      // asking whoever changed a limit to change this too. It reads the live
      // catalogue now, so a plan added or repriced in the admin console shows up
      // here without anybody remembering to come back.
      { kind: 'plan-limits' },
      {
        kind: 'p',
        body: 'The free plan stops at its included volume rather than billing an overage — there is no card on file to charge. Paid plans allow overage and price it per thousand.',
      },
      {
        kind: 'note',
        body: 'Test-mode sends never count against quota, so a test suite cannot exhaust a month of volume.',
      },
    ],
  },
];

export const DOC_INDEX = DOC_PAGES.map(({ slug, title }) => ({ slug, title }));
